import type { FcfEntitlementBar } from '@/features/decisions/decisions.api.types';

export function fmtMoneyM(usd: number | null | undefined, fractionDigits = 1): string {
  if (usd == null) return '—';
  const sign = usd < 0 ? '-' : '';
  const abs = Math.abs(usd);
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(fractionDigits)}B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(fractionDigits)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(fractionDigits)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

export function fmtMoneyMWithSign(usd: number | null | undefined): string {
  if (usd == null) return '—';
  const formatted = fmtMoneyM(usd, 1);
  if (usd > 0 && !formatted.startsWith('+') && !formatted.startsWith('-')) return `+${formatted}`;
  return formatted;
}

export function fmtInteger(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString();
}

// Signed days delta, e.g. -8.5 → "−8.5 days". Used for the days-of-supply change row.
export function fmtDaysWithSign(days: number | null | undefined, fractionDigits = 1): string {
  if (days == null) return '—';
  const sign = days > 0 ? '+' : days < 0 ? '−' : '';
  return `${sign}${Math.abs(days).toFixed(fractionDigits)} days`;
}

// Signed percentage-points delta from a ratio change, e.g. 0.023 → "+2.3 pts".
export function fmtPointsWithSign(ratioDelta: number | null | undefined, fractionDigits = 1): string {
  if (ratioDelta == null) return '—';
  const pts = ratioDelta * 100;
  const sign = pts > 0 ? '+' : pts < 0 ? '−' : '';
  return `${sign}${Math.abs(pts).toFixed(fractionDigits)} pts`;
}

export function weeksBetween(from: string | null | undefined, to: string | null | undefined): number | null {
  if (!from || !to) return null;
  const a = new Date(`${from}T00:00:00Z`).getTime();
  const b = new Date(`${to}T00:00:00Z`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return null;
  return Math.max(1, Math.round((b - a) / (7 * 24 * 60 * 60 * 1000)));
}

export function fmtDateRange(from: string | null | undefined, to: string | null | undefined): string {
  if (!from || !to) return '—';
  const fmt = (iso: string) => {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };
  return `${fmt(from)} – ${fmt(to)}`;
}

export interface DeltaBuildup {
  fcf_impact: number | null;
  revenue_retained_served_orders: number | null;
  revenue_from_backorder_recovery: number | null;
  production_cost_reduction: number | null;
  late_delivery_penalty_avoidance: number | null;
  inventory_dollars_freed: number | null;
  // Working capital / service level (issue #127). All comparison-minus-reference:
  // days-of-supply change in days (lower is better), average inventory at cost
  // change in dollars (lower is better), per-period availability change as a ratio
  // (higher is better; render as percentage points).
  days_of_supply_change: number | null;
  inventory_value_change: number | null;
  available_in_full_per_period_change: number | null;
}

function diff(b: number | null | undefined, a: number | null | undefined): number | null {
  if (b == null || a == null) return null;
  return b - a;
}

// Comparison-minus-reference deltas. Cost metrics are sign-flipped so positive = better.
export function computeDeltas(ref: FcfEntitlementBar, cmp: FcfEntitlementBar): DeltaBuildup {
  return {
    fcf_impact: diff(cmp.model_fcf, ref.model_fcf),
    revenue_retained_served_orders: diff(cmp.model_revenue_demand_fulfilled, ref.model_revenue_demand_fulfilled),
    revenue_from_backorder_recovery: diff(cmp.model_revenue_backlog_fulfilled, ref.model_revenue_backlog_fulfilled),
    production_cost_reduction: diff(ref.model_order_placed_total_cost, cmp.model_order_placed_total_cost),
    late_delivery_penalty_avoidance: diff(ref.model_backlog_total_penalty_cost, cmp.model_backlog_total_penalty_cost),
    inventory_dollars_freed: diff(ref.model_ending_onhand_value, cmp.model_ending_onhand_value),
    days_of_supply_change: diff(cmp.days_of_supply, ref.days_of_supply),
    inventory_value_change: diff(cmp.model_ending_onhand_value, ref.model_ending_onhand_value),
    available_in_full_per_period_change: diff(cmp.available_in_full_per_period, ref.available_in_full_per_period),
  };
}

export function parseExperimentIds(raw: string): string[] {
  return raw
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}
